import React, {
    useCallback, useContext, useEffect
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import _ from 'lodash';
import {
    SgSessionStorageService, SgScrollUtils, SgSubQuestionsContext
} from 'saga-modules';
import { AccordionContext } from '@jutro/components/_esm/widgets/accordion/AccordionContext';
import { TranslatorContext } from '@jutro/locale';
import { events } from 'gw-platform-events-js';
import CommonConstants from 'sg-common-constants';
import messages from 'sg-common-messages';
import metadata from './FormAccordionContent.metadata.json5';

function FormAccordionContent(props) {
    const {
        id,
        commonProps,
        customProps,
        onValidate: onValidateParent,
        components,
        accordionMetadata,
        accordionNumber,
        accordionTranslations,
        nextButtonId,
        isLastAccordion,
        additionalNextValidation,
        showNextOnOpen,
        setShowWizardContinue,
        setNumberOfAccordionsOpen,
        numberOfAccordionsOpen,
        handleCancel,
        preventAutoOpen,
        nextButtonAdditionalText,
        nextButtonContent,
        showConfirmation,
        isOpenAccordionNonPersistent,
        minNonPersistentOpenAccordion,
        hasPreviousAccordionsValid
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const { isAccordionOpen, toggleAccordionOpen } = useContext(AccordionContext);
    const translator = useContext(TranslatorContext);
    const subQuestionContext = {
        isComponentVisible: isAccordionOpen(`card${accordionNumber}`),
        isPreviousAccordionVisible: isAccordionOpen(`card${accordionNumber - 1}`)
    };

    useEffect(() => {
        if (!_.isNil(customProps) && customProps.openOnLoad
            && !isAccordionOpen(`card${accordionNumber}`)
        ) {
            toggleAccordionOpen(`card${accordionNumber}`);
        }
    }, []);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
        const isPreviousAccordionOpened = isAccordionOpen(`card${accordionNumber - 1}`);
        if (isComponentValid && !preventAutoOpen && !isAccordionOpen(`card${accordionNumber}`) && isPreviousAccordionOpened) {
            toggleAccordionOpen(`card${accordionNumber}`);
        }
    }, [id, isComponentValid, onValidateParent]);

    const openNextAccordion = useCallback(async () => {
        const prevAccordionsValid = !_.isNil(hasPreviousAccordionsValid) ? hasPreviousAccordionsValid : true;
        if (!isComponentValid || !prevAccordionsValid || SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS).length > 0
            || (!_.isNil(additionalNextValidation) && additionalNextValidation())) {
            commonProps.setShowErrors(true);
            events.emit(CommonConstants.SHOW_VALIDATION_ERROR_EVENT);
            events.emit(CommonConstants.ACCORDION_VALIDATION_ERROR_EVENT);
            return false;
        }
        if (!_.isNil(commonProps.setShowErrors)) {
            events.emit(CommonConstants.HIDE_VALIDATION_ERROR_EVENT);
            commonProps.setShowErrors(false);
        }
        toggleAccordionOpen(`card${accordionNumber + 1}`);
        setNumberOfAccordionsOpen(accordionNumber + 1);
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, commonProps.modelVM.value);
        SgScrollUtils.scrollToElementById(`card${accordionNumber + 1}`, 500);
        return true;
    }, [isComponentValid, commonProps, toggleAccordionOpen, accordionNumber, setNumberOfAccordionsOpen, hasPreviousAccordionsValid]);

    const closeCurrentAccordion = useCallback(() => {
        if (isAccordionOpen(`card${accordionNumber}`)) {
            toggleAccordionOpen(`card${accordionNumber}`);
        }
    }, [accordionNumber]);

    const resetProgressBarOnNonPersistentAccordionEvent = useCallback(() => {
        const isLastNonPersistentOpenAccordion = isAccordionOpen(`card${accordionNumber}`) && !(isAccordionOpen(`card${accordionNumber + 1}`));
        if (isLastNonPersistentOpenAccordion) {
            setNumberOfAccordionsOpen(minNonPersistentOpenAccordion - 1);
        }
    }, [accordionNumber, minNonPersistentOpenAccordion]);

    const closeNonPersistentAccordionEvent = useCallback(() => {
        closeCurrentAccordion();
        resetProgressBarOnNonPersistentAccordionEvent();
    }, []);

    useEffect(() => {
        if (isOpenAccordionNonPersistent) {
            events.on(CommonConstants.CLOSE_NON_PERSISTENT_ACCORDION_EVENT, closeNonPersistentAccordionEvent);
        }
        return () => {
            if (isOpenAccordionNonPersistent) {
                events.removeListener(CommonConstants.CLOSE_NON_PERSISTENT_ACCORDION_EVENT, closeNonPersistentAccordionEvent);
            }
        };
    }, []);

    const getComponentOverrides = () => {
        const overrides = [];
        _.each(components, (component) => {
            const objectName = `${component.name}`;
            overrides[objectName] = {
                ...commonProps,
                onValidate: onValidate,
                messageParams: accordionTranslations ? accordionTranslations.params : null,
                isNextAccordionOpen: isAccordionOpen(`card${accordionNumber + 1}`),
                ...component.componentProps,
            };
        });
        return overrides;
    };

    const getComponentsForMap = () => {
        return _.mapValues(components, (component) => {
            return component.controller;
        });
    };

    useEffect(() => {
        const openAccordionIfPossible = async () => {
            if (commonProps.canOpenAccordionOnEvent
                && commonProps.canOpenAccordionOnEvent(numberOfAccordionsOpen, accordionNumber) && isComponentValid) {
                await openNextAccordion();
            }
        };

        events.on(CommonConstants.OPEN_NEXT_ACCORDION_EVENT, openAccordionIfPossible);
        return () => {
            events.removeListener(CommonConstants.OPEN_NEXT_ACCORDION_EVENT, openAccordionIfPossible);
        };
    }, [numberOfAccordionsOpen, isComponentValid]);

    const overrideProps = {
        ...getComponentOverrides(),
        nextButtonContainer: {
            className: nextButtonAdditionalText ? 'sgAccordionNextButtonContainer sgNextButtonContainerAdditionalText'
                : 'sgAccordionNextButtonContainer'
        },
        nextButton: {
            content: translator(nextButtonContent || messages.accordionNextButton),
            visible: !isAccordionOpen(`card${accordionNumber + 1}`) && !isLastAccordion && showNextOnOpen !== false ? '' : false,
            id: nextButtonId || 'nextButton'
        },
        nextButtonAdditionalText: {
            content: nextButtonAdditionalText,
            visible: !isAccordionOpen(`card${accordionNumber + 1}`) && !isLastAccordion && nextButtonAdditionalText ? '' : false
        },
        cancelLink: {
            visible: !isAccordionOpen(`card${accordionNumber + 1}`) && !isLastAccordion && !!handleCancel,
            style: handleCancel ? { display: 'inline-block' } : { display: 'none' },
            onClick: handleCancel ? handleCancel.action : null,
            content: handleCancel ? handleCancel.messages : null,
            tabIndex: handleCancel ? 0 : -1
        },
        formAccordionContainer: {
            content: [accordionMetadata.componentContent]
        },
        confirmationMessageContainer: {
            visible: isAccordionOpen(`card${accordionNumber + 1}`) && !isLastAccordion && showConfirmation === true ? '' : false
        },
        confirmedMessage: {
            content: translator(messages.Confirmed)
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
            openNextAccordion,
            handleCancel
        },
        resolveComponentMap: {
            ...getComponentsForMap()
        }
    };

    if (isAccordionOpen(`card${accordionNumber}`)) {
        if (isLastAccordion && !_.isNil(setShowWizardContinue)) {
            setShowWizardContinue(isLastAccordion);
        }
    }
    return (
        <SgSubQuestionsContext.Provider value={subQuestionContext}>
            <ViewModelForm
                uiProps={metadata.componentContent}
                model={commonProps.modelVM}
                showErrors={commonProps.showErrors}
                onModelChange={commonProps.updateData}
                onValidationChange={onValidate}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </SgSubQuestionsContext.Provider>
    );
}

export default FormAccordionContent;
